import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import ApplicationContext from "domain/ApplicationContext";
import WorkOrder from "domain/WorkOrder";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useGetCustomers from "hooks/apollo/customer/useGetCustomers";
import { PublishWorkOrderDates, PublishWorkOrderDatesDryRun } from "hooks/apollo/publishWorkOrderDates.gql";
import { GetWorkOrders } from "hooks/apollo/workOrder/workOrder.gql";
import useTimeZone from "hooks/useTimeZone";
import React, { useEffect, useState } from "react";
import DateTime from "utils/DateTime";
import StringUtils from "utils/StringUtils";
import PublishWorkOrderDatesFormView from "./PublishWorkOrderDatesFormView";

const DEFAULT_RESPONSE_STATE = { errors: [], success: null };
const DEFAULT_START_DATE_STATE = DateTime.startOfDay(new Date()).toDate();
const DEFAULT_FILTER_STATE = {
  codes: [],
  customers: [],
  dueDateFrom: null,
  dueDateTo: null,
  startDate: DEFAULT_START_DATE_STATE,
  statuses: [],
};

export default function PublishWorkOrderDatesFormContainer() {
  const { overrideToSiteTimeZone } = useTimeZone();
  const applicationContext = useApplicationContext();

  const [response, setResponse] = useState(DEFAULT_RESPONSE_STATE);
  const resetResponse = () => setResponse(DEFAULT_RESPONSE_STATE);
  const [workOrderCountTotal, setWorkOrderCountTotal] = useState(0);
  const [workOrderCountOnInactiveLines, setWorkOrderCountOnInactiveLines] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);

  const [publishWorkOrderDates] = useMutation(PublishWorkOrderDates);
  const [getWorkOrderCounts, { loading }] = useLazyQuery(PublishWorkOrderDatesDryRun, {
    fetchPolicy: "network-only",
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only care about filters changing
  useEffect(() => {
    if (filters.startDate) {
      getWorkOrderCounts({
        variables: publishWorkOrderDatesInformation(filters, applicationContext, overrideToSiteTimeZone),
      }).then((result) => {
        const {
          publishWorkOrderDatesDryRun: { count, workOrdersOnInactiveLines },
        } = result.data;
        setWorkOrderCountTotal(count);
        setWorkOrderCountOnInactiveLines(workOrdersOnInactiveLines);
      });
    }
  }, [filters]);

  const { data: customerData } = useGetCustomers();
  const customers = customerData?.customers || [];
  const { OPEN, BOOKED } = WorkOrder.STATUS_TYPES;

  const { data: workOrderData } = useQuery(GetWorkOrders, {
    fetchPolicy: "no-cache",
    variables: { filters: { showScheduledCompletedOrCancelled: true, statuses: [OPEN, BOOKED] } },
  });

  const workOrders = workOrderData?.workOrders || [];

  const showWorkOrderCount = !loading && filters.startDate;

  const handleSave = () => {
    resetResponse();

    if (!confirmed(workOrderCountTotal)) return Promise.resolve();

    return publishWorkOrderDates({
      update: (_proxy, { data }) => {
        setResponse(data.publishWorkOrderDates.publishedWorkOrderDates);
      },
      variables: publishWorkOrderDatesInformation(filters, applicationContext, overrideToSiteTimeZone),
    });
  };

  const handleFilterChange = (updatedFields) => {
    resetResponse();
    setFilters((prevState) => ({ ...prevState, ...updatedFields }));
  };

  return (
    <PublishWorkOrderDatesFormView
      customers={customers}
      filters={filters}
      onFilterChange={handleFilterChange}
      onSave={handleSave}
      response={response}
      showWorkOrderCount={showWorkOrderCount}
      workOrderCountOnInactiveLines={workOrderCountOnInactiveLines}
      workOrderCountTotal={workOrderCountTotal}
      workOrders={workOrders}
    />
  );
}

function publishWorkOrderDatesInformation(filters, applicationContext, overrideToSiteTimeZone) {
  return {
    codes: filters.codes,
    customers: filters.customers,
    dueDateFrom: startOfDay(filters.dueDateFrom, applicationContext, overrideToSiteTimeZone),
    dueDateTo: endOfDay(filters.dueDateTo, applicationContext, overrideToSiteTimeZone),
    startsAt: startOfDay(filters.startDate, applicationContext, overrideToSiteTimeZone),
    statuses: filters.statuses,
  };
}

function startOfDay(date, applicationContext, overrideToSiteTimeZone) {
  if (!date) return null;

  return overrideToSiteTimeZone(ApplicationContext.startOfWorkDay(applicationContext, date));
}

function endOfDay(date, applicationContext, overrideToSiteTimeZone) {
  if (!date) return null;

  return overrideToSiteTimeZone(ApplicationContext.endOfWorkDay(applicationContext, date));
}

function confirmed(workOrderCountTotal) {
  return window.confirm(
    `Are you sure you want to update ${workOrderCountTotal} ${StringUtils.pluralize("work order", workOrderCountTotal)}?`,
  );
}
